import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ContactPopup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    businessType: [],
    jobsPerMonth: '',
    operationsManagement: '',
    crmTimeline: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState('idle');

  const businessTypeList = [
    'Solar Installation',
    'Lighting',
    'Multi-service Energy Business',
    'HVAC / Air Conditioning',
    'Hot Water Systems',
    'Other Energy Services',
  ];

  const jobsPerMonthOptions = [
    '1-10',
    '11-50',
    '51-100',
    '100+',
  ];

  const operationsManagementOptions = [
    'Manual Process',
    'Spreadsheets',
    'Basic Software',
    'Advanced Software',
  ];

  const crmTimelineOptions = [
    'Immediate (0-1 month)',
    'Soon (1-3 months)',
    'Planning (3-6 months)',
    'Future (6+ months)',
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCheckbox = (business) => {
    const updated = formData.businessType.includes(business)
      ? formData.businessType.filter(b => b !== business)
      : [...formData.businessType, business];
    setFormData(prev => ({ ...prev, businessType: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitted('sending');
    try {
      await axios.post('/api/contact', {
        fullName: formData.fullName,
        companyName: formData.companyName,
        email: formData.email,
        phone: formData.phone,
        businessType: formData.businessType,
        jobsPerMonth: formData.jobsPerMonth,
        operationsManagement: formData.operationsManagement,
        crmTimeline: formData.crmTimeline,
        message: formData.message,
      });
      setSubmitted('success');
    } catch (err) {
      setSubmitted('error');
    }
  };

  const handleClose = () => {
    setSubmitted('idle');
    setFormData({
      fullName: '', companyName: '', email: '',
      phone: '', businessType: [], jobsPerMonth: '', operationsManagement: '', crmTimeline: '', message: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600&family=DM+Sans:wght@300;400;500&display=swap');

        :root {
          --gold: #7ec8ec;
          --gold-light: #e8c97a;
          --dark: #0a0a0a;
          --dark-2: #111111;
          --dark-3: #1a1a1a;
          --surface: #161616;
          --border: rgba(201,168,76,0.2);
          --text: #f0ede6;
          --text-muted: #888;
          --error: #e05c5c;
          --radius: 4px;
        }

        .cp-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: overlayIn 0.4s ease;
        }

        @keyframes overlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .cp-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.82);
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .cp-modal {
          position: relative;
          background: var(--dark-2);
          border: 1px solid var(--border);
          width: 100%;
          max-width: 560px;
          max-height: 92vh;
          overflow-y: auto;
          border-radius: 2px;
          animation: modalIn 0.45s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,76,0.08);
          scrollbar-width: thin;
          scrollbar-color: var(--gold) transparent;
        }

        .cp-modal::-webkit-scrollbar { width: 3px; }
        .cp-modal::-webkit-scrollbar-track { background: transparent; }
        .cp-modal::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 2px; }

        @keyframes modalIn {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .cp-header {
          padding: 36px 36px 24px;
          border-bottom: 1px solid var(--border);
          position: relative;
        }

        .cp-accent-line {
          width: 40px;
          height: 1px;
          background: linear-gradient(90deg, var(--gold), transparent);
          margin-bottom: 16px;
        }

        .cp-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 28px;
          font-weight: 300;
          color: var(--text);
          letter-spacing: 0.02em;
          margin: 0 0 6px;
          line-height: 1.2;
        }

        .cp-title span { color: var(--gold); font-weight: 600; }

        .cp-subtitle {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: var(--text-muted);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin: 0;
        }

        .cp-close {
          position: absolute;
          top: 28px;
          right: 28px;
          background: none;
          border: 1px solid var(--border);
          color: var(--text-muted);
          width: 34px;
          height: 34px;
          border-radius: 2px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .cp-close:hover { border-color: var(--gold); color: var(--gold); background: rgba(201,168,76,0.06); }

        .cp-body { padding: 28px 36px 36px; }

        .cp-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
          margin-bottom: 14px;
        }

        .cp-field { display: flex; flex-direction: column; margin-bottom: 14px; }
        .cp-field:last-child { margin-bottom: 0; }

        .cp-label {
          font-family: 'DM Sans', sans-serif;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 8px;
          transition: color 0.2s;
        }

        .cp-field.focused .cp-label { color: var(--gold); }

        .cp-input, .cp-select, .cp-textarea {
          background: var(--surface);
          border: 1px solid rgba(255,255,255,0.07);
          color: var(--text);
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 300;
          padding: 13px 16px;
          border-radius: var(--radius);
          outline: none;
          transition: all 0.25s;
          width: 100%;
          box-sizing: border-box;
          -webkit-appearance: none;
        }

        .cp-input::placeholder, .cp-textarea::placeholder { color: rgba(255,255,255,0.2); }
        .cp-select option { background: var(--dark-3); color: var(--text); }

        .cp-input:focus, .cp-select:focus, .cp-textarea:focus {
          border-color: var(--gold);
          background: rgba(22,22,22,0.9);
          box-shadow: 0 0 0 3px rgba(201,168,76,0.08);
        }

        .cp-input.error, .cp-select.error, .cp-textarea.error {
          border-color: var(--error);
          box-shadow: 0 0 0 3px rgba(224,92,92,0.08);
        }

        .cp-select {
          cursor: pointer;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 16px center;
          padding-right: 40px;
        }

        .cp-textarea { resize: vertical; min-height: 90px; line-height: 1.5; }

        .cp-error-msg {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          color: var(--error);
          margin-top: 5px;
          letter-spacing: 0.04em;
        }

        .cp-consent {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: rgba(255,255,255,0.02);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          margin: 20px 0;
          cursor: pointer;
        }

        .cp-consent-check {
          width: 18px;
          height: 18px;
          min-width: 18px;
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 2px;
          background: var(--surface);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          margin-top: 1px;
          cursor: pointer;
        }

        .cp-consent-check.checked {
          background: var(--gold);
          border-color: var(--gold);
        }

        .cp-consent-check.checked::after {
          content: '✓';
          color: var(--dark);
          font-size: 11px;
          font-weight: 700;
        }

        .cp-consent-text {
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          color: var(--text-muted);
          line-height: 1.6;
        }

        .cp-consent-text a { color: var(--gold); text-decoration: none; }
        .cp-consent-text a:hover { text-decoration: underline; }

        .cp-submit {
          width: 100%;
          padding: 15px 24px;
          background: linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 50%, var(--gold) 100%);
          background-size: 200% 100%;
          color: var(--dark);
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          border: none;
          border-radius: var(--radius);
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .cp-submit:hover {
          background-position: right center;
          box-shadow: 0 8px 28px rgba(201,168,76,0.3);
          transform: translateY(-1px);
        }

        .cp-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .cp-submit:active { transform: translateY(0); }

        .cp-error-banner {
          background: #1a0a0a;
          border: 1px solid #5c1a1a;
          border-radius: var(--radius);
          padding: 14px 20px;
          color: #e05c5c;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          text-align: center;
          margin-top: 16px;
        }

        .cp-success {
          text-align: center;
          padding: 60px 36px;
          animation: fadeUp 0.5s ease;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .cp-success-icon {
          width: 64px;
          height: 64px;
          border: 1px solid var(--gold);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          font-size: 24px;
          color: var(--gold);
          animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) 0.1s both;
        }

        @keyframes scaleIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .cp-success-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 26px;
          font-weight: 300;
          color: var(--text);
          margin: 0 0 10px;
        }

        .cp-success-msg {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.7;
          margin: 0 0 32px;
        }

        .cp-success-btn {
          padding: 12px 32px;
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-muted);
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          border-radius: var(--radius);
          cursor: pointer;
          transition: all 0.2s;
        }
        .cp-success-btn:hover { border-color: var(--gold); color: var(--gold); }

        @media (max-width: 540px) {
          .cp-header, .cp-body { padding-left: 24px; padding-right: 24px; }
          .cp-header { padding-top: 28px; }
          .cp-body { padding-bottom: 28px; }
          .cp-row { grid-template-columns: 1fr; gap: 0; }
          .cp-title { font-size: 24px; }
          .cp-modal { max-height: 96vh; border-radius: 8px 8px 0 0; margin-top: auto; }
          .cp-overlay { align-items: flex-end; padding: 0; }
        }
      `}</style>

      <div className="cp-overlay">
        <div className="cp-backdrop" onClick={handleClose} />

        <div className="cp-modal" role="dialog" aria-modal="true">

          {/* ✅ Error banner — modal ke andar, upar */}
          {submitted === 'error' && (
            <div className="cp-error-banner">
              Something went wrong. Please try again.
            </div>
          )}

          {/* ✅ Success screen */}
          {submitted === 'success' ? (
            <div className="cp-success">
              <div className="cp-success-icon">✓</div>
              <h2 className="cp-success-title">Message Received</h2>
              <p className="cp-success-msg">
                Thank you for reaching out. Our team at InsightWide<br />
                will be in touch with you shortly.
              </p>
              <button className="cp-success-btn" onClick={handleClose}>Close</button>
            </div>
          ) : (
            <>
              <div className="cp-header">
                <div className="cp-accent-line" />
                <h2 className="cp-title">Get in <span>Touch</span></h2>
                <p className="cp-subtitle">InsightWide · Response within 24 hours</p>
                <button className="cp-close" onClick={handleClose} aria-label="Close">✕</button>
              </div>

              <div className="cp-body">
                <form onSubmit={handleSubmit} noValidate>

                  <div className="cp-row">
                    <div className="cp-field">
                      <label className="cp-label">Full Name *</label>
                      <input
                        className={`cp-input ${errors.fullName ? 'error' : ''}`}
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder="John Smith"
                        autoComplete="name"
                      />
                      {errors.fullName && <span className="cp-error-msg">{errors.fullName}</span>}
                    </div>

                    <div className="cp-field">
                      <label className="cp-label">Company Name *</label>
                      <input
                        className={`cp-input ${errors.companyName ? 'error' : ''}`}
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Your Company"
                        autoComplete="organization"
                      />
                      {errors.companyName && <span className="cp-error-msg">{errors.companyName}</span>}
                    </div>
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">Email Address *</label>
                    <input
                      className={`cp-input ${errors.email ? 'error' : ''}`}
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@company.com"
                      autoComplete="email"
                    />
                    {errors.email && <span className="cp-error-msg">{errors.email}</span>}
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">Phone Number *</label>
                    <input
                      className={`cp-input ${errors.phone ? 'error' : ''}`}
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+61 XXX XXX XXXX"
                      autoComplete="tel"
                    />
                    {errors.phone && <span className="cp-error-msg">{errors.phone}</span>}
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">What type of energy business do you operate?</label>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px'}}>
                      {businessTypeList.map((business) => (
                        <label key={business} style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', color: '#f0ede6', marginBottom: 0}}>
                          <input
                            type="checkbox"
                            checked={formData.businessType.includes(business)}
                            onChange={() => handleCheckbox(business)}
                            style={{width: '16px', height: '16px', cursor: 'pointer'}}
                          />
                          {business}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="cp-row">
                    <div className="cp-field">
                      <label className="cp-label">Jobs Per Month</label>
                      <select
                        className="cp-select"
                        name="jobsPerMonth"
                        value={formData.jobsPerMonth}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {jobsPerMonthOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>

                    <div className="cp-field">
                      <label className="cp-label">Operations Management</label>
                      <select
                        className="cp-select"
                        name="operationsManagement"
                        value={formData.operationsManagement}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {operationsManagementOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">CRM Implementation Timeline</label>
                    <select
                      className="cp-select"
                      name="crmTimeline"
                      value={formData.crmTimeline}
                      onChange={handleChange}
                    >
                      <option value="">Select Timeline</option>
                      {crmTimelineOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">How Can We Help? *</label>
                    <textarea
                      className="cp-textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your business and what you're looking for..."
                    />
                    {errors.message && <span className="cp-error-msg">{errors.message}</span>}
                  </div>

                  <button type="submit" className="cp-submit" disabled={submitted === 'sending'}>
                    {submitted === 'sending' ? 'Sending...' : 'Send Enquiry'}
                  </button>

                </form>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default ContactPopup;